"use client";

import { Fragment } from "react";
import { Landmark } from "lucide-react";
import type { GovernorHeroComponent } from "../../types";
import { RuntimeImage, RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";
import { getImageOverrideProps, mergeClassNames, resolveImageValue } from "../../utils/imageOptions";

/**
 * Governor detail hero — the same designed "panel" header the article pages use
 * (`ArticleHeroRenderer`), adapted to a governor's main data so the bio pages read
 * as part of the same design system instead of a bare title + floated photo.
 *
 * Layout:
 *   - a soft blue rounded panel, max 900px, centred;
 *   - desktop: two columns — text (breadcrumb → name Aleo H1 → yellow accent →
 *     structured meta row) on the left, the portrait on the right, both vertically
 *     centred against each other;
 *   - mobile: a single centred stack — breadcrumb, name, accent, portrait, then the
 *     meta centred below the picture.
 *
 * The portrait is shown WHOLE (`h-auto`, natural ratio — no crop), because an
 * official governor portrait should be fully visible; the upper-body crop/zoom is
 * only used on the small "Historique récent" strip cards, not on this showcase.
 *
 * The meta row echoes the article's "avatar · author · date": a brand-blue badge
 * (a Landmark glyph — the APA building) then the governor's rank ("15ème
 * Gouverneur", blue, like the author name) and tenure ("depuis 2025" / "2021 –
 * 2025", navy, like the date). Rank/tenure are derived from `ordre` +
 * `dateDebut`/`dateFin` when the CMS carries no explicit `subtitle`/`meta`, so a
 * freshly-added governor record renders a complete header with no hand-typed labels.
 *
 * The body RichText is a sibling block that flows full-width BELOW this panel.
 */
export function GovernorHeroRenderer({ component }: { component: GovernorHeroComponent }) {
  const locale = useLocale();
  const { title, image, imageAlt } = component.content;
  const subtitle = (component.content.subtitle ?? "").trim() || formatOrdinal(component.content.ordre);
  const meta =
    (component.content.meta ?? "").trim() ||
    formatYearRange(component.content.dateDebut, component.content.dateFin);

  const { src, options } = resolveImageValue(image);
  const hasImage = Boolean(src);
  const alt = imageAlt || title || "";

  // Breadcrumb trail. Governors are not a category (so no translated category
  // crumb like the article); the trail is the fixed site location of the roster.
  const crumbs: { label: string; href: string }[] = [
    { label: "La Banque Centrale", href: "/la-banque" },
    { label: "Gouverneurs", href: "/la-banque/gouverneurs" },
  ];

  return (
    <section className="bg-[#EEF1F0] md:bg-[#E6F4EA] pb-6 pt-6 md:pt-10 lg:pt-10 lg:pb-8 md:rounded-xl px-4 md:px-6 lg:px-10">
      <div className="rounded-3xl" style={{ maxWidth: 900, margin: "0 auto" }}>
        <div className="grid gap-6 md:gap-8 md:grid-cols-[minmax(0,1.15fr)_minmax(260px,0.85fr)] md:items-start">
          {/* TEXT COLUMN — centred stack on mobile (content sits below the centred
              picture); left-aligned + TOP-aligned on desktop (like the article hero)
              via the grid's items-start, with the whole portrait beside it. */}
          <div className="space-y-4 text-center md:text-left">
            <nav
              aria-label={t(locale, "breadcrumbAriaLabel")}
              className="flex flex-wrap items-center justify-center gap-2 text-sm xl:text-base font-semibold text-[#6B7280] md:justify-start"
            >
              {crumbs.map((crumb, index) => (
                <Fragment key={`${crumb.label}-${index}`}>
                  {index > 0 && <span className="text-[#A1A1AA]">›</span>}
                  <RuntimeLink href={crumb.href} className="hover:text-[#00B862]">
                    {crumb.label}
                  </RuntimeLink>
                </Fragment>
              ))}
            </nav>

            <h1
              className="text-3xl font-semibold text-[#00B862] md:text-4xl"
              style={{ fontFamily: "var(--font-aleo)" }}
            >
              {title}
            </h1>
            <div className="mt-2 h-1 w-12 rounded-sm bg-[#FFC105] mx-auto md:mx-0" aria-hidden="true" />

            {/* MOBILE PORTRAIT: centred, between the title and the meta. */}
            {hasImage ? (
              <div className="mt-6 block md:hidden">
                <GovernorPortrait src={src as string} alt={alt} options={options} className="max-w-[320px] mx-auto" />
              </div>
            ) : null}

            {/* META ROW — mirrors the article "avatar · author · date" rhythm.
                Centred below the picture on mobile, left-aligned on desktop.
                Rendered only when there is a rank or tenure to show. */}
            {subtitle || meta ? (
              <div className="flex items-center justify-center gap-3 pt-2 md:justify-start md:pt-4 text-sm xl:text-base text-[#52525B]">
                <div
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-gradient-to-b from-[#00A356] to-[#00B862] text-white ring-2 ring-white shadow-[0_2px_8px_-2px_rgba(0,184,98,0.45)]"
                  aria-hidden="true"
                >
                  <Landmark className="h-5 w-5" />
                </div>
                <div className="min-w-0 text-left">
                  {subtitle ? (
                    <p
                      className="text-[#00B862]"
                      style={{
                        fontFamily: '"Nunito Sans"',
                        fontSize: "18px",
                        fontWeight: 700,
                        lineHeight: "127.5%",
                      }}
                    >
                      {subtitle}
                    </p>
                  ) : null}
                  {meta ? <p className="mt-0.5 font-medium text-[#121212]">{meta}</p> : null}
                </div>
              </div>
            ) : null}
          </div>

          {/* IMAGE COLUMN (md+): the whole portrait, right-aligned, vertically
              centred against the text via the grid's items-center. */}
          {hasImage ? (
            <div className="hidden md:flex md:justify-end">
              <GovernorPortrait src={src as string} alt={alt} options={options} className="max-w-[270px]" />
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

/** Governor portrait shown WHOLE — natural ratio (`h-auto`), no crop — so an
 *  official portrait is always fully visible. The caller sets the width cap +
 *  alignment via `className`. */
function GovernorPortrait({
  src,
  alt,
  options,
  className,
}: {
  src: string;
  alt: string;
  options: ReturnType<typeof resolveImageValue>["options"];
  className: string;
}) {
  const overrideProps = getImageOverrideProps(options);
  return (
    <RuntimeImage
      src={src}
      alt={alt}
      className={mergeClassNames(`h-auto w-full rounded border border-[#E5E7EB] ${className}`, overrideProps.className)}
      style={overrideProps.style}
      priority
    />
  );
}

/** "Nème Gouverneur" / "1er Gouverneur" / "Intérim" (ordre 0) / "" for missing.
 *  Same rules as GovernorListRenderer so the detail hero and the roster card
 *  label a governor identically. */
function formatOrdinal(ordre?: string | null): string {
  const raw = (ordre ?? "").toString().trim();
  if (raw === "") return "";
  const n = Number(raw);
  if (!Number.isFinite(n)) return "";
  if (n === 0) return "Intérim";
  if (n === 1) return "1er Gouverneur";
  return `${n}ème Gouverneur`;
}

/** "1997 – 2013" / "depuis 2025" / "jusqu'en 2013" / "". */
function formatYearRange(start?: string | null, end?: string | null): string {
  const sy = pickYear(start);
  const ey = pickYear(end);
  if (sy && ey) return `${sy} – ${ey}`;
  if (sy && !ey) return `depuis ${sy}`;
  if (!sy && ey) return `jusqu'en ${ey}`;
  return "";
}

function pickYear(d?: string | null): string {
  const m = (d ?? "").toString().match(/(\d{4})/);
  return m ? m[1] : "";
}
